import Immutable from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import * as CheckoutActions from 'app/actions/CheckoutActions';
import * as HoldActions from 'app/actions/HoldActions';
import * as BorrowingActions from 'app/actions/BorrowingActions';
import { selectBibEntities } from 'app/selectors/EntitiesSelector';
import { WORKFLOW_VIEWS } from 'app/constants/HoldsConstants';
import { selectCurrentAccount } from 'app/selectors/AuthSelector';
import {
  selectPickupBranches,
  selectActivePreferredBranches,
  selectCurrentLibrary
} from 'app/selectors/LibrarySelector';
import { selectActiveHoldsByMetadataId, selectHoldsTransactions } from 'app/selectors/HoldsSelector';
import { selectCheckoutsByMetadataId } from 'app/selectors/CheckoutSelector';
import { selectShelvesForCurrentUser } from 'app/selectors/ShelfSelector';
import accountShape from 'app/shapes/accountShape';
import {
  ItemLevelHoldsWorkflowView,
  PhysicalHoldsWorkflowView,
  SingleCancelHoldWorkflowView,
  DigitalCheckoutWorkflowView,
  DigitalHoldsWorkflowView,
  DigitalMissingFormatWorkflowView
} from '../shared/ItemTransactions';
import SinglePauseHoldWorkflowView from '../borrowing/holds/PauseHoldWorkflowView/components/SinglePauseHoldWorkflowView';

export class ItemTransactionWorkflowContainer extends React.PureComponent {
  render() {
    const {
      bib,
      holdsId,
      initialSuspendStartDate,
      initialSuspendEndDate,
      enableStartDate,
      enableEndDate,
      closeWorkflow,
      branches,
      preferredBranches,
      isLoading,
      itemPosition,
      itemPositionGrouped,
      totalCount,
      currentAccount,
      accountId,
      shelves,
      currentLocale,
      holdActions,
      checkoutActions,
      requiresExpiryDate,
      holdInfoMessages,
      fieldErrors,
      provider,
      formats,
      checkout,
      digitalNotificationEmail,
      view,
      materialPolicy,
      selectedBranch,
      itemGroups,
      itemIdsOnHold,
      successfulItemIds,
      failedItemIds,
      singleBranch,
      showVolumeSelectorCollection,
      showVolumeSelectorDueDate,
      showVolumeSelectorStatus,
      isFetchingItemGroups,
      borrowingActions,
      messagingId,
      workflowId,
      holdsTransactions,
      reciprocalRank
    } = this.props;

    switch (view) {
      case WORKFLOW_VIEWS.CANCEL_HOLD:
        return (
          <SingleCancelHoldWorkflowView
            messagingId={messagingId}
            workflowId={workflowId || bib.get('metadataId')}
            bib={bib}
            holdId={holdsId}
            cancelHold={holdActions.cancelHold}
            handleClose={closeWorkflow}
            isLoading={isLoading || holdsTransactions.getIn([holdsId, 'isCancelling'], false)}
            accountId={accountId}
            shelves={shelves}
            fieldErrors={fieldErrors}
          />
        );
      case WORKFLOW_VIEWS.ITEM_LEVEL_HOLD:
        return (
          <ItemLevelHoldsWorkflowView
            messagingId={messagingId}
            workflowId={workflowId || bib.get('metadataId')}
            bib={bib}
            branches={branches}
            preferredBranches={preferredBranches}
            currentAccount={currentAccount}
            dispatch={this.props.dispatch}
            handleClose={closeWorkflow}
            selectedBranch={selectedBranch}
            placeItemHold={holdActions.placeItemHold}
            fetchItemGroups={holdActions.getItemGroups}
            isLoading={isLoading}
            fieldErrors={fieldErrors}
            itemGroups={itemGroups}
            itemIdsOnHold={itemIdsOnHold}
            successfulItemIds={successfulItemIds}
            failedItemIds={failedItemIds}
            supportsBatchVolumeHolds={materialPolicy.get('supportsBatchVolumeHolds')}
            showVolumeSelectorCollection={showVolumeSelectorCollection}
            showVolumeSelectorStatus={showVolumeSelectorStatus}
            showVolumeSelectorDueDate={showVolumeSelectorDueDate}
            isFetchingItemGroups={isFetchingItemGroups}
            provider={provider}
          />
        );
      case WORKFLOW_VIEWS.DIGITAL_MISSING_FORMAT: {
        return (
          <DigitalMissingFormatWorkflowView
            messagingId={messagingId}
            workflowId={workflowId || bib.get('metadataId')}
            lockInFormat={checkoutActions.lockInFormat}
            formats={formats}
            bib={bib}
            checkout={checkout}
            provider={provider}
            accountId={accountId}
            isLoading={isLoading}
            handleClose={closeWorkflow}
            fetchDigitalFormats={checkoutActions.fetchDigitalFormats}
          />
        );
      }
      case WORKFLOW_VIEWS.DIGITAL_CHECKOUT:
        return (
          <DigitalCheckoutWorkflowView
            bib={bib}
            holdId={holdsId}
            workflowId={workflowId || bib.get('metadataId')}
            messagingId={messagingId}
            handleClose={closeWorkflow}
            accountId={accountId}
            isLoading={isLoading}
            itemPosition={itemPosition}
            itemPositionGrouped={itemPositionGrouped}
            totalCount={totalCount}
            holdActions={holdActions}
            borrowingActions={borrowingActions}
            digitalCheckout={checkoutActions.digitalCheckout}
            provider={provider}
            requireFormats={materialPolicy.get('requiresFormatDuringCheckout')}
            fieldErrors={fieldErrors}
            formats={formats}
            fetchDigitalFormats={checkoutActions.fetchDigitalFormats}
            quickStartUrl={materialPolicy.get('quickStartUrl')}
            showQuickStartLink={materialPolicy.get('showQuickstartLink')}
            reciprocalRank={reciprocalRank}
          />
        );
      case WORKFLOW_VIEWS.DIGITAL_HOLD:
        return (
          <DigitalHoldsWorkflowView
            bib={bib}
            provider={provider}
            isLoading={isLoading}
            itemPosition={itemPosition}
            itemPositionGrouped={itemPositionGrouped}
            totalCount={totalCount}
            handleClose={closeWorkflow}
            formats={formats}
            showFormatDuringHold={materialPolicy.get('showFormatDuringHold')}
            requiresFormatDuringHold={materialPolicy.get('requiresFormatDuringHold')}
            quickStartUrl={materialPolicy.get('quickStartUrl')}
            showQuickStartLink={materialPolicy.get('showQuickstartLink')}
            accountId={accountId}
            placeHold={holdActions.placeHold}
            digitalNotificationEmail={digitalNotificationEmail}
            fetchDigitalFormats={checkoutActions.fetchDigitalFormats}
            workflowId={workflowId}
            messagingId={messagingId}
            reciprocalRank={reciprocalRank}
          />
        );
      case WORKFLOW_VIEWS.PAUSE_HOLD:
        return (
          <SinglePauseHoldWorkflowView
            bib={bib}
            messagingId={messagingId || holdsId}
            workflowId={workflowId || holdsId}
            holdId={holdsId}
            initialSuspendStartDate={initialSuspendStartDate}
            initialSuspendEndDate={initialSuspendEndDate}
            enableStartDate={enableStartDate}
            enableEndDate={enableEndDate}
            pauseHolds={holdActions.pauseOrResumeHolds}
            handleClose={closeWorkflow}
            isLoading={isLoading}
            accountId={accountId}
            fieldErrors={fieldErrors}
          />
        );
      default:
        return (
          <PhysicalHoldsWorkflowView
            bib={bib}
            branches={branches}
            itemPosition={itemPosition}
            itemPositionGrouped={itemPositionGrouped}
            totalCount={totalCount}
            preferredBranches={preferredBranches}
            handleClose={closeWorkflow}
            placeHold={holdActions.placeHold}
            currentAccount={currentAccount}
            accountId={accountId}
            singleBranch={singleBranch}
            currentLocale={currentLocale}
            requiresExpiryDate={requiresExpiryDate}
            holdInfoMessages={holdInfoMessages}
            isLoading={isLoading}
            fieldErrors={fieldErrors}
            workflowId={workflowId}
            messagingId={messagingId}
            reciprocalRank={reciprocalRank}
          />
        );
    }
  }
}

ItemTransactionWorkflowContainer.propTypes = {
  bib: ImmutablePropTypes.map,
  dispatch: PropTypes.func.isRequired,
  materialPolicy: ImmutablePropTypes.map.isRequired,
  holdsId: PropTypes.string,
  closeWorkflow: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  itemPosition: PropTypes.number,
  itemPositionGrouped: PropTypes.number,
  totalCount: PropTypes.number,
  holdInfoMessages: ImmutablePropTypes.listOf(PropTypes.number).isRequired,
  currentAccount: accountShape.isRequired,
  accountId: PropTypes.number.isRequired,
  shelves: ImmutablePropTypes.map.isRequired,
  branches: ImmutablePropTypes.listOf(ImmutablePropTypes.map).isRequired,
  currentLocale: PropTypes.string,
  selectedBranch: PropTypes.string,
  preferredBranches: ImmutablePropTypes.list.isRequired,
  singleBranch: PropTypes.bool,
  holdActions: PropTypes.objectOf(PropTypes.func).isRequired,
  checkoutActions: PropTypes.objectOf(PropTypes.func).isRequired,
  borrowingActions: PropTypes.objectOf(PropTypes.func).isRequired,
  requiresExpiryDate: PropTypes.bool.isRequired,
  fieldErrors: PropTypes.objectOf(
    PropTypes.shape({
      classification: PropTypes.string,
      message: PropTypes.string
    })
  ),
  provider: PropTypes.string.isRequired,
  formats: ImmutablePropTypes.listOf(
    ImmutablePropTypes.contains({
      name: PropTypes.string,
      hasDownloadLink: PropTypes.bool
    })
  ),
  checkout: ImmutablePropTypes.map,
  digitalNotificationEmail: PropTypes.string,
  view: PropTypes.string,
  itemGroups: ImmutablePropTypes.listOf(
    ImmutablePropTypes.mapContains({
      volume: PropTypes.string,
      collection: PropTypes.string.isRequired,
      copy: PropTypes.string,
      itemId: PropTypes.string.isRequired,
      callNumber: PropTypes.string
    })
  ),
  itemIdsOnHold: ImmutablePropTypes.listOf(PropTypes.string),
  failedItemIds: ImmutablePropTypes.listOf(PropTypes.number),
  successfulItemIds: ImmutablePropTypes.listOf(PropTypes.number),
  showVolumeSelectorCollection: PropTypes.bool,
  showVolumeSelectorStatus: PropTypes.bool,
  showVolumeSelectorDueDate: PropTypes.bool,
  isFetchingItemGroups: PropTypes.bool,
  messagingId: PropTypes.string,
  workflowId: PropTypes.string,
  reciprocalRank: PropTypes.number,
  holdsTransactions: ImmutablePropTypes.map,
  enableEndDate: PropTypes.bool,
  enableStartDate: PropTypes.bool,
  initialSuspendEndDate: PropTypes.string,
  initialSuspendStartDate: PropTypes.string
};

ItemTransactionWorkflowContainer.displayName = 'ItemTransactionWorkflowContainer';

const mapStateToProps = (state, ownProps) => {
  const account = selectCurrentAccount(state, ownProps) || Immutable.Map();
  const library = selectCurrentLibrary(state);
  const policy = state.getIn(['entities', 'bibs', ownProps.metadataId, 'policy'], Immutable.Map());
  const branches = selectPickupBranches(state, ownProps);
  const checkouts = selectCheckoutsByMetadataId(state).get(ownProps.metadataId, Immutable.List());

  const holds = selectActiveHoldsByMetadataId(state).get(ownProps.metadataId, Immutable.List());
  const holdsId = ownProps.holdsId || (holds.size === 1 ? holds.first().get('holdsId') : null);
  const initialSuspendStartDate = holds.size === 1 ? holds.first().get('suspendStartDate') : null;
  const initialSuspendEndDate = holds.size === 1 ? holds.first().get('suspendEndDate') : null;
  const bibs = selectBibEntities(state);
  return {
    bib: state.getIn(['entities', 'bibs', ownProps.metadataId, 'briefInfo']),
    enableStartDate: bibs.getIn([ownProps.metadataId, 'policy', 'materialPolicy', 'supportsSuspendHoldStartDateEdit']),
    enableEndDate: bibs.getIn([ownProps.metadataId, 'policy', 'materialPolicy', 'supportsSuspendHoldEndDateEdit']),
    itemGroups: state.getIn(['entities', 'bibs', ownProps.metadataId, 'itemGroups'], Immutable.List()),
    holdsId,
    initialSuspendStartDate,
    initialSuspendEndDate,
    itemIdsOnHold: holds.map(hold => hold.get('itemId')).toList(),
    checkout: checkouts.first(),
    formats: state.getIn(['entities', 'bibs', ownProps.metadataId, 'policy', 'formats'], null),
    branches,
    preferredBranches: selectActivePreferredBranches(state),
    currentAccount: account,
    provider: policy.get('provider'),
    materialPolicy: policy.get('materialPolicy', Immutable.Map()),
    accountId: account.get('id'),
    shelves: selectShelvesForCurrentUser(state),
    singleBranch: library.get('singleBranch'),
    selectedBranch: state.getIn(['ui', 'itemTransactions', ownProps.metadataId, 'selectedBranch']),
    isLoading: state.getIn(['ui', 'itemTransactions', ownProps.metadataId, 'isLoading']),
    currentLocale: state.getIn(['localization', 'currentLanguage'], 'en-US'),
    requiresExpiryDate: library.get('requiresHoldExpiryDate', false),
    holdInfoMessages: library.get('holdInfoMessages', Immutable.List()),
    availableForCheckout:
      state.getIn(['entities', 'bibs', ownProps.metadataId, 'availability', 'status'], false) === 'AVAILABLE',
    digitalNotificationEmail: account.get('digitalNotificationEmail'),
    failedItemIds: state.getIn(['ui', 'itemTransactions', ownProps.metadataId, 'failedItemIds'], Immutable.List()),
    successfulItemIds: state.getIn(
      ['ui', 'itemTransactions', ownProps.metadataId, 'successfulItemIds'],
      Immutable.List()
    ),
    showVolumeSelectorCollection: library.get('showVolumeSelectorCollection'),
    showVolumeSelectorStatus: library.get('showVolumeSelectorStatus'),
    showVolumeSelectorDueDate: library.get('showVolumeSelectorDueDate'),
    isFetchingItemGroups: state.getIn(['ui', 'itemLevelHold', ownProps.metadataId, 'isFetching']),
    holdsTransactions: selectHoldsTransactions(state)
  };
};

const mapDispatchToProps = dispatch => ({
  holdActions: bindActionCreators(HoldActions, dispatch),
  checkoutActions: bindActionCreators(CheckoutActions, dispatch),
  borrowingActions: bindActionCreators(BorrowingActions, dispatch),
  dispatch
});

export default connect(mapStateToProps, mapDispatchToProps)(ItemTransactionWorkflowContainer);
